import { Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { GiftCardPurchase } from '../gift-card-purchase.interface';
import { emailValidator } from 'src/app/shared/email-validator/email.validator';
import { CustomAmountSettings, Design } from '../design-and-amount-settings';
import { currencyValidator } from 'src/app/shared/currency-validator/currency.validator';
import { Subject, Observable } from 'rxjs';

@Injectable()
export class GiftCardPurchaseFormService {
    private formSubmitted = new Subject<void>();

    form: FormGroup;

    get senderName() {
        return this.form.get('senderName');
    }
    get senderEmail() {
        return this.form.get('senderEmail');
    }
    get recipientName() {
        return this.form.get('recipientName');
    }
    get recipientEmail() {
        return this.form.get('recipientEmail');
    }
    get message() {
        return this.form.get('message');
    }
    get itemTotal() {
        return this.form.get('itemTotal');
    }
    get itemImagePath() {
        return this.form.get('itemImagePath');
    }
    get giftCardDesignID() {
        return this.form.get('giftCardDesignID');
    }

    constructor(private fb: FormBuilder) {}

    createForm(): FormGroup {
        return (this.form = this.fb.group({
            senderFirstName: ['', Validators.required],
            senderLastName: [''],
            senderEmail: ['', [emailValidator, Validators.required]],
            recipientName: [''],
            recipientEmail: [''],
            message: [''],
            itemTotal: [''],
            itemImagePath: [''],
            giftCardDesignID: ['', Validators.required],
        }));
    }

    getData(): GiftCardPurchase {
        return {
            senderFirstName: this.form.controls.senderFirstName.value,
            senderLastName: this.form.controls.senderLastName.value,
            senderEmail: this.form.controls.senderEmail.value,
            recipientEmail: this.form.controls.recipientEmail.value
                ? this.form.controls.recipientEmail.value
                : this.form.controls.senderEmail.value,
            recipientName: this.form.controls.recipientName.value
                ? this.form.controls.recipientName.value
                : this.form.controls.senderFirstName.value,
            itemNote: this.form.controls.message.value,
            itemTotal: this.form.controls.itemTotal.value,
            itemImagePath: this.form.controls.itemImagePath.value,
            giftCardDesignID: this.form.controls.giftCardDesignID.value,
        };
    }

    setData(obj: GiftCardPurchase) {
        this.form.patchValue({
            senderFirstName: obj.senderFirstName,
            senderLastName: obj.senderLastName,
            senderEmail: obj.senderEmail,
            recipientName: obj.recipientName,
            recipientEmail: obj.recipientEmail,
            message: obj.itemNote,
            itemTotal: obj.itemTotal,
            itemImagePath: obj.itemImagePath,
            giftCardDesignID: obj.giftCardDesignID,
        });
    }

    setItemTotalValidators(
        customAmountSettings: CustomAmountSettings,
        suggestedAmounts: number[]
    ) {
        const itemTotalValidators = [Validators.required, currencyValidator()];

        if (customAmountSettings.isEnabled) {
            this.setItemTotalMinValidator(
                customAmountSettings,
                itemTotalValidators
            );
            this.setItemTotalMaxValidator(
                customAmountSettings,
                itemTotalValidators
            );
        }

        if (
            suggestedAmounts
                .map((x) => x.toString())
                .includes(this.form.controls.itemTotal.value)
        ) {
            this.removeItemTotalValidation();
        } else {
            this.addItemTotalValidation(itemTotalValidators);
        }
    }

    private removeItemTotalValidation() {
        this.form.controls.itemTotal.clearValidators();
        this.form.controls.itemTotal.updateValueAndValidity();
    }

    private setItemTotalMaxValidator(
        customAmountSettings: CustomAmountSettings,
        itemTotalValidators: any[]
    ) {
        if (customAmountSettings.maximum) {
            itemTotalValidators.push(
                Validators.max(customAmountSettings.maximum)
            );
        }
    }

    private setItemTotalMinValidator(
        customAmountSettings: CustomAmountSettings,
        itemTotalValidators: any[]
    ) {
        if (customAmountSettings.minimum) {
            itemTotalValidators.push(
                Validators.min(customAmountSettings.minimum)
            );
        } else {
            itemTotalValidators.push(Validators.min(1));
        }
    }

    private addItemTotalValidation(itemTotalValidators: any[]) {
        this.form.controls.itemTotal.setValidators(itemTotalValidators);
        this.form.controls.itemTotal.updateValueAndValidity();
    }

    setRecipientOptionValidators() {
        this.form.controls.recipientName.setValidators([Validators.required]);
        this.form.controls.recipientEmail.setValidators([
            Validators.required,
            emailValidator,
        ]);
        this.form.controls.recipientEmail.updateValueAndValidity();
        this.form.controls.recipientName.updateValueAndValidity();
    }

    clearRecipientOptionValidators() {
        this.form.controls.recipientEmail.clearValidators();
        this.form.controls.recipientName.clearValidators();
        this.form.controls.recipientEmail.patchValue('');
        this.form.controls.recipientName.patchValue('');
        this.form.controls.recipientEmail.updateValueAndValidity();
        this.form.controls.recipientName.updateValueAndValidity();
    }

    selectDesign(design: Design) {
        this.giftCardDesignID.setValue(design.id);
        this.itemImagePath.setValue(design.image);
    }

    isValid(): boolean {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return false;
        }

        return true;
    }

    submitForm() {
        this.formSubmitted.next();
    }

    formSubmitted$(): Observable<void> {
        return this.formSubmitted.asObservable();
    }
}
